<script setup lang="tsx">
import { CirclePlus, Fold } from "@element-plus/icons-vue";
import { useStorage } from "@vueuse/core";
import { Column, ElButton, SortBy, TableV2SortOrder } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { EntityDefinition, EntityDefinitionListDto } from "~/api";
import ComplianceQuery from "~/Compliance/components/ComplianceQuery.vue";
import EntityDefinitionEditor from "~/Compliance/components/EntityDefinitionEditor.vue";
import TableWrapper from "~/components/TableWrapper.vue";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { formatDate } from "~/utils/dateUtils";
import { useStableAutoResizer } from "~/utils/resizeObserverFix.simple";
const entityDefinitionStore = useEntityDefinitionStore();

type listItemType = EntityDefinitionListDto & {
  parentName: string;
};
type mainType = EntityDefinition;

const loading = ref(true);

const items = ref<listItemType[]>([]);
const selectedRow = ref<listItemType | undefined>({} as listItemType);
const drawerOpen = ref(false);
const myInfoStore = useMyInfoStore();

// Prevent ResizeObserver performance issues
const { autoResizerRef } = useStableAutoResizer();

const extendList = (
  entityDefinitionListDtoArray: EntityDefinitionListDto[],
) => {
  return entityDefinitionListDtoArray.map((entityDefinitionListDto) => {
    const parentEntity = entityDefinitionListDto.parentEntityDefinitionGuid
      ? entityDefinitionListDtoArray.find(
          (e) => e.guid === entityDefinitionListDto.parentEntityDefinitionGuid,
        )
      : undefined;
    return {
      ...entityDefinitionListDto,
      parentName: parentEntity?.name ?? "",
    };
  });
};

const includePublicEntities = useStorage("show-public-entities", true);

onMounted(async () => {
  await entityDefinitionStore.load();
  items.value = extendList(entityDefinitionStore.EntitiesDefinitionList);
  loading.value = false;
  sortResults();
});

const extractListItem = (item: mainType): listItemType => {
  // similar to creating the DTO on the server
  return {
    name: item.name,
    guid: item.guid,
    status: item.status,
    icon: item.icon ?? "",
    madePublicAt: item.madePublicAt,
    parentName: "",
  };
};

//*** start Sorting */
const sortState = ref<SortBy>({ key: 0, order: TableV2SortOrder.ASC });
const lastState = ref<SortBy>({ key: 0, order: TableV2SortOrder.DESC });
const onSort = (params: SortBy) => {
  sortState.value = params;
  sortResults();
};

const sortResults = () => {
  const { key, order } = sortState.value;
  if (key) {
    const delta = order === TableV2SortOrder.ASC ? 1 : -1;
    lastState.value = { key, order };
    items.value.sort((a, b) => {
      return (a[key as keyof listItemType] ?? "") >
        (b[key as keyof listItemType] ?? "")
        ? delta
        : -delta;
    });
  } else {
    items.value.sort((a, b) => {
      const nameA = (a.parentName ? a.parentName + " / " : "") + a.name || "";
      const nameB = (b.parentName ? b.parentName + " / " : "") + b.name || "";
      return nameA.localeCompare(nameB);
    });
  }
};

//*** end Sorting */

let numCreated = 0;
const createItem = () => {
  selectedRow.value = { guid: "new" + numCreated++ } as listItemType;
  drawerOpen.value = true;
};

/// Show the clicked item in the drawer
const showItem = (row: listItemType) => {
  selectedRow.value = row;
  drawerOpen.value = true;
};

const handleNewItemAdded = (item: mainType) => {
  items.value.push(extractListItem(item));
  sortResults();
};

const handleItemUpdated = (item: mainType) => {
  const index = items.value.findIndex((f) => f.guid === item.guid);
  if (index !== -1) {
    items.value[index] = extractListItem(item);
  }
  sortResults();
};

const closeDrawer = () => {
  drawerOpen.value = false;
  selectedRow.value = undefined;
};

/*** for querying/filtering the listing ***/
// Need to fix
const complianceQueryInput = ref<{
  type: string[];
  importance: string[];
  status: string[];
  search: "";
}>({
  type: [],
  importance: [],
  status: [],
  search: "",
});

const refreshList = async () => {
  loading.value = true;
  await entityDefinitionStore.load(true);
  items.value = extendList(entityDefinitionStore.EntitiesDefinitionList);
  loading.value = false;
};

const itemsFiltered = computed(() => {
  const searchFiltered = complianceQueryInput.value.search !== "";
  const statusFiltered = complianceQueryInput.value.status.length !== 0;

  return items.value.filter((item) => {
    if (searchFiltered) {
      if (
        !item.name?.toLowerCase().includes(complianceQueryInput.value.search)
      ) {
        return false;
      }
    }
    if (statusFiltered) {
      if (
        item.status &&
        !complianceQueryInput.value.status.includes(item.status)
      ) {
        return false;
      }
    }
    if (!includePublicEntities.value) {
      if (item.madePublicAt !== null) {
        return false;
      }
    }
    return true;
  });
});

const tempColumns: Column<any>[] = [
  {
    title: "Entity Type",
    key: "name",
    dataKey: "name",
    width: 300,

    cellRenderer: (row: any) => {
      return (
        <ElButton
          text
          onClick={() => showItem(row.rowData)}
          icon={Fold}
          type="primary"
          style="font-size: 15px;"
        >
          {row.rowData.parentName ? row.rowData.parentName + " / " : ""}
          {row.rowData.name}
        </ElButton>
      );
    },
  },
  {
    title: "Icon",
    key: "icon",
    dataKey: "icon",
    width: 50,
    cellRenderer: (row: any) => {
      return <i class={"mdi mdi-" + row.rowData.icon} />;
    },
  },
  {
    title: "Home Page",
    key: "homePage",
    dataKey: "homePage",
    width: 200,
    cellRenderer: (row: any) => {
      const homePage = row.rowData.homePage;
      return <span>{homePage ?? "-"}</span>;
    },
  },
  {
    title: "Public?",
    key: "madePublicAt",
    dataKey: "madePublicAt",
    align: "center",
    width: 100,
    cellRenderer: (row: any) => {
      return (
        <span title={formatDate(row.rowData.madePublicAt, 1)}>
          {row.rowData.madePublicAt ? "Public" : ""}
        </span>
      );
    },
  },
];

const columns = computed(() =>
  tempColumns.map((col) => {
    return {
      ...col,
      sortable: true,
    };
  }),
);
</script>

<template>
  <section>
    <div class="flexRow">
      <span class="showingInTable">
        Showing {{ itemsFiltered.length }} of
        {{ items.length }}
      </span>
      <RefreshButton @click="refreshList" />
      <ComplianceQuery @submit:query="complianceQueryInput = $event">
        <template #create-button> </template>
      </ComplianceQuery>
      <el-checkbox
        v-model="includePublicEntities"
        label="Include public entities"
      />

      <el-button
        v-if="myInfoStore.hasSetupBasicRole"
        class="addButton"
        :icon="CirclePlus"
        title="Create a new entity"
        @click="createItem"
      >
        New Entity
      </el-button>
    </div>
  </section>

  <!-- TABLE FOR COMPLIANCE ITEMS -->
  <ElAutoResizer ref="autoResizerRef" v-loading="loading">
    <template #default="{ height, width }">
      <TableWrapper
        v-if="!loading"
        ref="mainTableRef"
        :columns="columns"
        :data="itemsFiltered"
        row-key="guid"
        :height="height"
        :width="width"
        :sort-state="sortState"
        @column-sort="onSort"
      >
      </TableWrapper>
    </template>
  </ElAutoResizer>

  <EntityDefinitionEditor
    v-if="selectedRow?.guid"
    :key="selectedRow.guid"
    :entity-definition-list-dto="selectedRow"
    @close-drawer="closeDrawer"
    @added="handleNewItemAdded"
    @updated="handleItemUpdated"
    @deleted="refreshList"
  ></EntityDefinitionEditor>
</template>
